import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import MedicalConditions from './components/MedicalConditions';
import Medications from './components/Medications';
import Allergies from './components/Allergies';
import Prescriptions from './components/Prescriptions';
import Vaccinations from './components/Vaccinations';
import GrowthTracker from './components/GrowthTracker';
import BirthRecord from './components/BirthRecord';
import MedicalSessions from './components/MedicalSessions';
import Memories from './components/Memories';
import Insurance from './components/Insurance';
import BabySetup from './components/BabySetup';
import MainLogin from './components/MainLogin';
import DependentLogin from './components/DependentLogin';
import DependentDashboard from './components/DependentDashboard';
import JoinSync from './components/JoinSync';
import HealthcareLogin from './components/HealthcareLogin';
import AdminDashboard from './components/AdminDashboard';
import FrontDeskDashboard from './components/FrontDeskDashboard';
import DoctorDashboard from './components/DoctorDashboard';
import PrescriptionView from './components/PrescriptionView';
import CentralAdminLogin from './components/CentralAdminLogin';
import CentralAdminDashboard from './components/CentralAdminDashboard';
import DirectBooking from './components/DirectBooking';
import ReportsUpload from './components/ReportsUpload';
import DoctorLogin from './components/DoctorLogin';
import FrontDeskLogin from './components/FrontDeskLogin';
import MedicalAssistantLogin from './components/MedicalAssistantLogin';
import MedicalAssistantDashboard from './components/MedicalAssistantDashboard';
import PrivacyPolicy from './components/PrivacyPolicy';
import { ViewOnlyProvider, useViewOnly } from './contexts/ViewOnlyContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// New component to handle QR code guest parameter
function QRAccessHandler({ onAccessSuccess, onAccessComplete }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { enableViewOnlyMode } = useViewOnly();
  const hasProcessedRef = React.useRef(false);

  React.useEffect(() => {
    const checkQRAccess = async () => {
      // Check if URL has ?guest= parameter (using 'guest' instead of 'access' to avoid conflict with emergent-main.js)
      const params = new URLSearchParams(location.search);
      const accessBabyId = params.get('guest');
      
      // Skip QR processing for healthcare and admin routes
      const isHealthcareRoute = location.pathname.includes('/healthcare-') || 
                               location.pathname.includes('/admin-') || 
                               location.pathname.includes('/frontdesk-') || 
                               location.pathname.includes('/doctor-') ||
                               location.pathname.includes('/medical-assistant-') ||
                               location.pathname.includes('/central-admin-') ||
                               location.pathname === '/doctor-login' ||
                               location.pathname === '/frontdesk-login' ||
                               location.pathname === '/medical-assistant-login';
      
      if (isHealthcareRoute && !accessBabyId) {
        // For healthcare routes without guest parameter, just complete immediately
        if (!hasProcessedRef.current) {
          hasProcessedRef.current = true;
          onAccessComplete();
        }
        return;
      }
      
      // Only process once using ref to avoid re-renders
      if (accessBabyId && !hasProcessedRef.current) {
        hasProcessedRef.current = true;
        
        try {
          // Call the access/view endpoint to get baby data
          const response = await axios.post(`${API}/access/view`, {
            baby_id: accessBabyId
          });
          
          const babyData = response.data.baby;
          
          // Store baby in sessionStorage as view-only
          sessionStorage.setItem('viewOnlyBaby', JSON.stringify(babyData));
          
          // Enable view-only mode
          enableViewOnlyMode('Guest');
          
          // Call the success callback to set currentBaby in App
          onAccessSuccess(babyData);
          
          toast.success(`Viewing ${babyData.name}'s health record`);
          
          // Navigate to dashboard and remove query parameter
          navigate('/', { replace: true });
          
          // Notify parent that QR check is complete
          onAccessComplete();
        } catch (error) {
          console.error('Error accessing baby via QR code:', error);
          toast.error('Invalid or expired access link');
          // Only redirect to login if not on healthcare routes
          if (!isHealthcareRoute) {
            navigate('/login', { replace: true });
          }
          onAccessComplete();
        }
      } else if (!accessBabyId && !hasProcessedRef.current) {
        // No access parameter, notify parent immediately
        hasProcessedRef.current = true;
        onAccessComplete();
      }
    };
    
    checkQRAccess();
  }, [location.search, location.pathname, navigate, enableViewOnlyMode, onAccessSuccess, onAccessComplete]);

  return null; // This component doesn't render anything
}

// Wrapper component to check QR access and conditionally render routes  
function AppRoutes({ currentBaby, handleLoginSuccess, createBaby }) {
  const location = useLocation();
  const hasGuestParam = new URLSearchParams(location.search).has('guest');
  
  // If there's a guest parameter and no baby yet, show a loading screen instead of redirecting to login
  if (hasGuestParam && !currentBaby) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Accessing health record...</p>
        </div>
      </div>
    );
  }
  
  return (
    <Routes>
      {/* Landing Page - Root route when not logged in */}
      <Route path="/" element={currentBaby ? <Dashboard baby={currentBaby} /> : <LandingPage />} />
      
      {/* Login and Setup routes - accessible without baby */}
      <Route path="/login" element={currentBaby ? <Navigate to="/" /> : <MainLogin onLoginSuccess={handleLoginSuccess} />} />
      <Route path="/setup" element={currentBaby ? <Navigate to="/" /> : <BabySetup onBabyCreated={createBaby} />} />
      
      {/* Dependent routes */}
      <Route path="/dependent-login" element={<DependentLogin />} />
      <Route path="/dependent-dashboard" element={<DependentDashboard />} />
      <Route path="/join-sync" element={<JoinSync />} />
      
      {/* Healthcare Management routes */}
      <Route path="/healthcare-login" element={<HealthcareLogin />} />
      <Route path="/doctor-login" element={<DoctorLogin />} />
      <Route path="/frontdesk-login" element={<FrontDeskLogin />} />
      <Route path="/medical-assistant-login" element={<MedicalAssistantLogin />} />
      <Route path="/admin-dashboard" element={<AdminDashboard />} />
      <Route path="/frontdesk-dashboard" element={<FrontDeskDashboard />} />
      <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
      <Route path="/medical-assistant-dashboard" element={<MedicalAssistantDashboard />} />
      
      {/* Central Admin routes */}
      <Route path="/central-admin-login" element={<CentralAdminLogin />} />
      <Route path="/central-admin-dashboard" element={<CentralAdminDashboard />} />
      
      {/* Prescription View - Public route for QR code access */}
      <Route path="/prescription/:sessionId" element={<PrescriptionView />} />
      
      {/* Direct Booking - Public route for appointment booking */}
      <Route path="/direct-booking" element={<DirectBooking />} />
      
      {/* Reports Upload - Public route for uploading test reports */}
      <Route path="/reports-upload" element={<ReportsUpload />} />
      
      {/* Privacy Policy - Public route */}
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      
      {/* Protected routes - require baby */}
      {currentBaby && (
        <>
          <Route path="/medical-conditions" element={<MedicalConditions baby={currentBaby} />} />
          <Route path="/medications" element={<Medications baby={currentBaby} />} />
          <Route path="/allergies" element={<Allergies baby={currentBaby} />} />
          <Route path="/prescriptions" element={<Prescriptions baby={currentBaby} />} />
          <Route path="/vaccinations" element={<Vaccinations baby={currentBaby} />} />
          <Route path="/growth-tracker" element={<GrowthTracker baby={currentBaby} />} />
          <Route path="/birth-record" element={<BirthRecord baby={currentBaby} />} />
          <Route path="/medical-sessions" element={<MedicalSessions baby={currentBaby} />} />
          <Route path="/memories" element={<Memories baby={currentBaby} />} />
          <Route path="/insurance" element={<Insurance baby={currentBaby} />} />
        </>
      )}
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [currentBaby, setCurrentBaby] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qrCheckComplete, setQrCheckComplete] = useState(false);
  const [waitingForQR, setWaitingForQR] = useState(false);

  useEffect(() => {
    // Check if URL has guest parameter
    const urlParams = new URLSearchParams(window.location.search);
    const hasGuestParam = urlParams.has('guest');
    
    if (hasGuestParam) {
      // Mark that we're waiting for QR processing
      setWaitingForQR(true);
    }
    
    checkForExistingBaby();
  }, []);

  const checkForExistingBaby = async () => {
    try {
      // Check for view-only baby (from dependent access or QR code)
      const viewOnlyBaby = sessionStorage.getItem('viewOnlyBaby');
      if (viewOnlyBaby) {
        try {
          const parsed = JSON.parse(viewOnlyBaby);
          // Validate the parsed data
          if (parsed && parsed.id && parsed.name) {
            setCurrentBaby(parsed);
          } else {
            sessionStorage.removeItem('viewOnlyBaby');
            setCurrentBaby(null);
          }
        } catch (e) {
          sessionStorage.removeItem('viewOnlyBaby');
          setCurrentBaby(null);
        }
        setLoading(false);
        return;
      }

      // Check for logged in baby in localStorage
      const storedBaby = localStorage.getItem('currentBaby');
      if (storedBaby) {
        try {
          const babyData = JSON.parse(storedBaby);
          
          // Validate baby data structure before API call
          if (!babyData || !babyData.id || babyData.id === 'undefined' || babyData.id === 'null') {
            // Invalid baby data, clear and show landing page
            localStorage.removeItem('currentBaby');
            setCurrentBaby(null);
            setLoading(false);
            return;
          }
          
          // Fetch fresh data from API to verify baby still exists
          const response = await axios.get(`${API}/babies/${babyData.id}`);
          const freshBabyData = response.data;
          
          // Update localStorage with fresh data
          localStorage.setItem('currentBaby', JSON.stringify(freshBabyData));
          setCurrentBaby(freshBabyData);
        } catch (error) {
          console.error('Error fetching fresh baby data:', error);
          // Baby doesn't exist or API error - clear localStorage and show landing page
          localStorage.removeItem('currentBaby');
          setCurrentBaby(null);
        }
        
        setLoading(false);
        return;
      }

      // No baby logged in - user needs to login or QR access will handle it
      setCurrentBaby(null);
      setLoading(false);
    } catch (error) {
      console.error('Error checking for baby:', error);
      // Clear any invalid data
      localStorage.removeItem('currentBaby');
      setCurrentBaby(null);
      setLoading(false);
    }
  };

  const createBaby = async (babyData) => {
    try {
      const response = await axios.post(`${API}/babies`, babyData);
      setCurrentBaby(response.data);
      // Store in localStorage
      localStorage.setItem('currentBaby', JSON.stringify(response.data));
      // Clear synced device flag for new owner account
      localStorage.removeItem('isSyncedDevice');
      
      // Initialize vaccination schedule
      await axios.post(`${API}/vaccinations/initialize/${response.data.id}`);
      
      return response.data;
    } catch (error) {
      console.error('Error creating baby:', error);
      throw error;
    }
  };

  const handleLoginSuccess = (baby) => {
    setCurrentBaby(baby);
    // Store in localStorage to persist login
    localStorage.setItem('currentBaby', JSON.stringify(baby));
    // Clear synced device flag for owner login
    localStorage.removeItem('isSyncedDevice');
    // Enable view-only mode for baby/patient
    enableViewOnlyMode(baby.name);
  };

  const handleLogout = () => {
    setCurrentBaby(null);
    localStorage.removeItem('currentBaby');
    sessionStorage.clear();
  };

  const handleQRAccessComplete = () => {
    setQrCheckComplete(true);
    setLoading(false);
  };

  if (loading && !waitingForQR) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ViewOnlyProvider>
        <BrowserRouter>
          {/* QR Access Handler - checks for ?guest= parameter */}
          <QRAccessHandler 
            onAccessSuccess={(baby) => setCurrentBaby(baby)}
            onAccessComplete={handleQRAccessComplete}
          />
          
          <AppRoutes 
            currentBaby={currentBaby}
            handleLoginSuccess={handleLoginSuccess}
            createBaby={createBaby}
          />
          
          <Toaster position="top-right" />
        </BrowserRouter>
      </ViewOnlyProvider>
    </div>
  );
}

export default App;